import { useFormik } from 'formik';
import { Link } from 'gatsby';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Container, Form, InputGroup, Row } from 'react-bootstrap';
import VerificationInput from 'react-verification-input';
import useFormSender from './useFormSender';
import useVerificationSender from './useVerificationSender';
import { useTheme } from 'src/hooks';
import useReCaptcha from 'src/hooks/useReCaptcha';
import getCookie from 'src/utils/get-cookie';

function WebFormAws() {
  const formStepContainer = useRef(null);
  const [pageStep, setPageStep] = useState('showForm');
  const [numberOfTries, setNumberOfTries] = useState(0);
  const [tryAgainMsg, setTryAgainMsg] = useState(false);
  const reCaptcha = useReCaptcha('awsBookFormSubmit');
  const verificationSender = useVerificationSender({ setPageStep });
  const formSender = useFormSender({ setPageStep, setTryAgainMsg, numberOfTries, setNumberOfTries });

  useEffect(() => {
    if (pageStep !== 'showForm') {
      formStepContainer.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [pageStep]);

  const t = useTheme();
  const initialValues = {
    name: '',
    companyName: '',
    email: '',
    phone: '',
    cloudServiceProvider: '',
    message: '',
    gdpr: false,
    date: new Date(),
    hubspotutk: '',
  };

  const onSubmit = async (formData) => {
    const success = await reCaptcha.verify(formData);
    if (!success) {
      return setPageStep('fatalError');
    }
    verificationSender.send(`+36${formData.phone}`);
  };

  const { handleSubmit, handleChange, values } = useFormik({ initialValues, onSubmit });

  const onSubmitSms = (formData) => {
    const data = {
      form: {
        ...values,
        phone: `+36${values.phone}`,
        hubspotutk: getCookie('hubspotutk'),
      },
      code: formData.smsVerificationCode,
    };

    formSender.send(data);
  };

  const formikSMS = useFormik({ initialValues: { smsVerificationCode: '' }, onSubmit: onSubmitSms });

  const formContainer = (
    <Container>
      <Row>
        <Col xs={12} md={{ span: 8, offset: 2 }} lg={{ span: 6, offset: 3 }}>
          <Col>
            <h2 className="h2 pb-5 text-center text-white">A könyv letöltéséhez kérjük töltse ki az alábbi űrlapot:</h2>
          </Col>
          <Col>
            <Form onSubmit={handleSubmit} data-hs-ignore="true">
              {/* Name */}
              <Form.Group controlId="name" className="mb-4">
                <Form.Label>Név *</Form.Label>
                <Form.Control required type="text" placeholder="Név" name="name" value={values.name} onChange={handleChange} />
              </Form.Group>

              {/* Email */}
              <Form.Group controlId="email" className="mb-4">
                <Form.Label>E-mail *</Form.Label>
                <Form.Control required type="text" placeholder="E-mail" name="email" value={values.email} onChange={handleChange} />
              </Form.Group>

              {/* Company Name */}
              <Form.Group controlId="companyName" className="mb-4">
                <Form.Label>Cégnév</Form.Label>
                <Form.Control type="text" placeholder="Cégnév" name="companyName" value={values.companyName} onChange={handleChange} />
              </Form.Group>

              {/* Phone */}
              <Form.Group controlId="phone" className="mb-4">
                <Form.Label>Mobiltelefonszám *</Form.Label>
                <InputGroup>
                  <InputGroup.Text style={{ borderRadius: `.25rem 0 0 .25rem` }}>+36</InputGroup.Text>
                  <Form.Control
                    style={{ borderRadius: `0 .25rem .25rem 0` }}
                    required
                    type="tel"
                    placeholder="00 000 0000"
                    name="phone"
                    value={values.phone}
                    onChange={handleChange}
                    pattern="^(?:20|30|50|70)\s?(?:\d\s?){6}\d$"
                  />
                </InputGroup>
                <Form.Text muted>
                  A formátum a következő: +36 00 000 0000.
                  <br />
                  Kérjük valódi telefonszámot adjon meg, mert SMS segítségével validáljuk a telefonszámot.
                </Form.Text>
              </Form.Group>

              {/* Cloud service */}
              <Form.Group className="mb-4">
                <Form.Label className="d-block">Használ-e bármilyen fajta felhőszolgáltatást? Ha igen, akkor melyiket? *</Form.Label>
                <div className="custom-control custom-radio custom-control-inline mr-5">
                  <input
                    className="custom-control-input"
                    type="radio"
                    id="cloud-1"
                    name="cloudServiceProvider"
                    value="none"
                    checked={values.cloudServiceProvider.includes('none')}
                    onChange={handleChange}
                    required
                  />
                  <label className="custom-control-label text-white" htmlFor="cloud-1">
                    Nem
                  </label>
                </div>
                <div className="custom-control custom-radio custom-control-inline mr-5">
                  <input
                    className="custom-control-input"
                    type="radio"
                    id="cloud-2"
                    name="cloudServiceProvider"
                    value="aws"
                    checked={values.cloudServiceProvider.includes('aws')}
                    onChange={handleChange}
                  />
                  <label className="custom-control-label text-white" htmlFor="cloud-2">
                    Igen, AWS-t
                  </label>
                </div>
                <div className="custom-control custom-radio custom-control-inline mr-5">
                  <input
                    className="custom-control-input"
                    type="radio"
                    id="cloud-3"
                    name="cloudServiceProvider"
                    value="not_aws"
                    checked={values.cloudServiceProvider.includes('not_aws')}
                    onChange={handleChange}
                  />
                  <label className="custom-control-label text-white" htmlFor="cloud-3">
                    Igen, de nem AWS-t
                  </label>
                </div>
              </Form.Group>

              {/* Message */}
              <Form.Group controlId="message" className="mb-4">
                <Form.Label>Amennyiben használ felhőszolgáltatást, kérjük írja le a felhasználási módját:</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Válasz"
                  name="message"
                  value={values.message}
                  onChange={handleChange}
                  as="textarea"
                  maxLength={255}
                  rows={4}
                />
              </Form.Group>

              {/* GDPR */}
              <Form.Group controlId="gdpr" className="mb-4">
                <div className="custom-control custom-checkbox">
                  <input
                    className="custom-control-input"
                    type="checkbox"
                    id="gdpr"
                    name="gdpr"
                    checked={values.gdpr}
                    onChange={handleChange}
                    required
                  />
                  <label className="custom-control-label" htmlFor="gdpr" aria-label="Consent to management of my data">
                    <span className="text-size-sm text-white">
                      <span className="mr-1">{t.form.gdprConsent}</span>
                      <Link to={`/${t.navigation.gdpr.path}`} className="text-secondary font-weight-semi-bold">
                        {t.navigation.gdpr.label}
                      </Link>
                      <span className="ml-1">*</span>
                    </span>
                  </label>
                </div>
              </Form.Group>
              <div className="d-flex justify-content-center mt-8">
                <Button variant="gradient-secondary" type="submit" disabled={verificationSender.isLoading}>
                  Tovább
                  {verificationSender.isLoading && <span> ...</span>}
                </Button>
              </div>
            </Form>
          </Col>
        </Col>
      </Row>
    </Container>
  );

  const smsVerificationForm = (
    <Container className="pt-8" key={numberOfTries}>
      <Row>
        <Col xs={12} md={{ span: 8, offset: 2 }} lg={{ span: 6, offset: 3 }}>
          <Col className="mb-6">
            <h2 className="font-weight-normal text-size-lg text-uppercase text-center text-white">Mobiltelefonszám ellenőrzése</h2>
            <div className="h2 pb-5 text-center text-white">Küldtünk Neked egy SMS kódot</div>
            <div className="text-center text-white">A telefonszám, amit megadtál: +36{values.phone}</div>
            {numberOfTries > 0 ? <p className="text-yellow text-center">Helytelen SMS-kódot adtál meg, próbáld meg újra.</p> : null}
          </Col>
          <Col>
            <form onSubmit={formikSMS.handleSubmit}>
              <Form.Group controlId="sms-code" className="mb-4">
                <Form.Label>SMS Kód</Form.Label>
                <VerificationInput
                  length={6}
                  validChars="0-9"
                  placeholder="_"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  onChange={(value) => formikSMS.setFieldValue('smsVerificationCode', value)}
                  inputProps={{
                    name: 'code',
                    required: true,
                    minLength: 6,
                    maxLength: 6,
                    type: 'tel',
                    autoComplete: 'off',
                    className: 'border-0 visually-hidden',
                  }}
                  classNames={{
                    character: 'form-control form-control-hover-remove h-auto d-flex justify-content-center align-items-center',
                    container: 'h-auto w-100',
                    characterSelected: 'form-input-focus',
                  }}
                />
                <style>{`.vi__wrapper { width: 100%; } .vi__character { aspect-ratio: 1; }`}</style>
              </Form.Group>
              <div className="d-flex justify-content-center mt-8">
                <Button variant="gradient-secondary" type="submit" disabled={formSender.isLoading}>
                  Jóváhagyás
                  {formSender.isLoading && <span> ...</span>}
                </Button>
              </div>
              {tryAgainMsg && (
                <div className="mt-3">
                  <p className="text-center text-white">Kerjük ellenőrizd le, hogy helyes-e a megadott kód.</p>
                </div>
              )}
            </form>
          </Col>
        </Col>
      </Row>
    </Container>
  );

  const fatalError = (
    <Container className="pt-8">
      <Row>
        <Col xs={12} md={{ span: 8, offset: 2 }} lg={{ span: 6, offset: 3 }}>
          <Col className="mb-6">
            <h2 className="text-size-lg text-uppercase text-center text-white">Rendszerhiba</h2>
            {numberOfTries > 2 ? (
              <p className="text-yellow text-center">
                háromszor érvénytelen kódot adtál meg! Próbálkozz újra később vagy vedd fel velünk a kapcsolatot.
              </p>
            ) : null}
            <p className="text-center text-white">
              Az automatikus AWS Könyv elérési folyamat megszakadt.
              <br />
              Kérjük vedd fel velünk a kapcsolatot az&nbsp;
              <a className="font-weight-bold text-secondary" href="mailto:aws@cheppers.com">
                aws@cheppers.com
              </a>
              &nbsp;e-mail címen keresztül &quot;AWS Könyv&quot; tárggyal.
            </p>
            <p className="pb-5 text-center text-white">Köszönjük!</p>
          </Col>
        </Col>
      </Row>
    </Container>
  );

  const currentStep = () => {
    switch (pageStep) {
      case 'showForm':
        return formContainer;
      case 'showSmsVerification':
        return smsVerificationForm;
      case 'fatalError':
        return fatalError;
      default:
        throw new Error('Should have returend one of the steps.');
    }
  };

  return (
    <Container ref={formStepContainer} fluid className="bg-gray-800 px-0 py-8">
      {currentStep()}
    </Container>
  );
}

export default WebFormAws;
